import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/payFileCreate.mutation"

import type {
  CreatePayFileFailure,
  CreatePayFileInput,
  CreatePayFileMutation,
  CreatePayFileMutationVariables,
} from "@/graphql/codegen/graphql"
import { CreatePayFileDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCreatePayFileReturn {
  createPayFile: (input: CreatePayFileInput) => Promise<boolean>
  isCreating: boolean
}

export const useCreatePayFile = (): UseCreatePayFileReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<CreatePayFileMutation, CreatePayFileMutationVariables>(
    CreatePayFileDocument
  )

  const createPayFile = useCallback(
    async (input: CreatePayFileInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.payFile.create
      const hasError = error || output?.__typename === "CreatePayFileFailure"

      if (hasError) {
        const message = error?.message ?? (output as CreatePayFileFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Your Pay File has been created" })
      return true
    },
    [executeMutation, notify]
  )

  return { createPayFile, isCreating: fetching }
}
